import { useThree } from "@react-three/fiber";
import React, { useEffect, useMemo, useState } from "react";
import {
  Easing,
  continueRender,
  delayRender,
  interpolate,
  staticFile,
  useCurrentFrame,
} from "remotion";
import { CanvasTexture, Color, SRGBColorSpace, Texture } from "three";
import {
  PHONE_GLOW_END_OFFSET,
  PHONE_GLOW_PEAK_OFFSET,
  PHONE_GLOW_START_FRAME,
  PHONE_SCREEN_SWAP_FRAME,
  PROCESSING_END_FRAME,
  PROCESSING_START_FRAME,
} from "./helpers/timeline";
import {
  PHONE_CURVE_SEGMENTS,
  PHONE_LAY_TILT,
  PHONE_SHININESS,
  PhoneLayout,
} from "./helpers/layout";
import { roundedRect } from "./helpers/rounded-rectangle";
import { RoundedBox } from "./RoundedBox";

// Phone tilt + camera config: see PHONE_LAY_TILT in helpers/layout.ts and
// <ThreeCanvas> in PhotoParsingScene.tsx.

const PRE_UPLOAD_SCREEN_SRC = staticFile("images/app-screen-input.png");
const TIMER_READY_SCREEN_SRC = staticFile("images/app-screen-timer-ready.png");

// The phone's on-screen content swaps when the cursor "drops" the captured
// workout screenshot onto the phone screen. See timeline.ts for sequencing.
const SCREEN_SWAP_FRAME = PHONE_SCREEN_SWAP_FRAME;

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (event) => reject(event);
    img.src = src;
  });

// Resolve the phone-screen Textures BEFORE Remotion captures any frame.
// Because:
//   1. The image fetches happen at module-load (so they start ASAP).
//   2. delayRender blocks the renderer until continueRender fires.
//   3. The Textures are stored at module scope so Phone's very first render
//      reads them synchronously — no setState/useEffect → re-render race.
//
// `npx remotion still` was snapshotting the canvas after the initial commit
// but before component effects had pushed loaded textures into state, so
// state-driven loading produced a blank screen.
let preUploadTextureCache: Texture | null = null;
let timerReadyTextureCache: Texture | null = null;
const screenTexturesReady = new Set<() => void>();

if (typeof window !== "undefined") {
  const handle = delayRender("Loading phone screen textures");
  Promise.all([
    loadImage(PRE_UPLOAD_SCREEN_SRC),
    loadImage(TIMER_READY_SCREEN_SRC),
  ])
    .then(([preUploadImage, timerReadyImage]) => {
      // Preprocess each screenshot through a 2D canvas with brightness
      // filter BEFORE uploading to the GPU. A simple color multiplier in
      // the shader can't push past 1.0 because the framebuffer clamps;
      // baking the lift into the source pixels actually shifts dark-mode
      // UI into a brighter range. Saturation untouched so purple UI
      // accents stay purple instead of shifting to pink.
      const buildTexture = (img: HTMLImageElement) => {
        const canvas = new OffscreenCanvas(
          img.naturalWidth,
          img.naturalHeight,
        );
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          throw new Error("Failed to get 2D context for screen texture");
        }
        ctx.filter = "brightness(1.55)";
        ctx.drawImage(img, 0, 0);
        const tex = new CanvasTexture(canvas);
        tex.colorSpace = SRGBColorSpace;
        tex.needsUpdate = true;
        return tex;
      };
      preUploadTextureCache = buildTexture(preUploadImage);
      timerReadyTextureCache = buildTexture(timerReadyImage);
      screenTexturesReady.forEach((cb) => cb());
      continueRender(handle);
    })
    .catch((err) => {
      console.error("[Phone] failed to load screen images", err);
      continueRender(handle);
    });
}

export const Phone: React.FC<{
  readonly phoneColor: string;
  readonly phoneLayout: PhoneLayout;
  readonly worldOffset?: [number, number, number];
}> = ({ phoneColor, phoneLayout, worldOffset = [0, 0, 0] }) => {
  const { invalidate } = useThree();

  const screenGeometry = useMemo(() => {
    return roundedRect({
      width: phoneLayout.screen.width,
      height: phoneLayout.screen.height,
      radius: phoneLayout.screen.radius,
    });
  }, [
    phoneLayout.screen.height,
    phoneLayout.screen.radius,
    phoneLayout.screen.width,
  ]);

  // ShapeGeometry assigns UVs from local vertex positions (range 0..W, 0..H),
  // so a unit-spanning texture needs repeat = 1 / world-size on each axis.
  // Mutating the cached Textures during render is safe — they're shared refs
  // and the layout values are stable per props snapshot.
  if (preUploadTextureCache) {
    preUploadTextureCache.repeat.x = 1 / phoneLayout.screen.width;
    preUploadTextureCache.repeat.y = 1 / phoneLayout.screen.height;
  }
  if (timerReadyTextureCache) {
    timerReadyTextureCache.repeat.x = 1 / phoneLayout.screen.width;
    timerReadyTextureCache.repeat.y = 1 / phoneLayout.screen.height;
  }

  // For headless rendering, delayRender already held the renderer until the
  // textures cached, so the very first render reads them. For Studio's
  // interactive path the promise may resolve after Phone mounts — subscribe
  // to the ready set so the component re-renders once textures land.
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    if (preUploadTextureCache && timerReadyTextureCache) {
      invalidate();
      return;
    }
    const onReady = () => {
      forceUpdate((n) => n + 1);
      invalidate();
    };
    screenTexturesReady.add(onReady);
    // Promise may have resolved between render and effect — re-check.
    if (preUploadTextureCache && timerReadyTextureCache) {
      onReady();
    }
    return () => {
      screenTexturesReady.delete(onReady);
    };
  }, [invalidate]);

  const frame = useCurrentFrame();
  const activeTexture =
    frame >= SCREEN_SWAP_FRAME ? timerReadyTextureCache : preUploadTextureCache;

  const glowProgress = interpolate(
    frame,
    [
      PHONE_GLOW_START_FRAME,
      PHONE_GLOW_START_FRAME + PHONE_GLOW_PEAK_OFFSET,
      PHONE_GLOW_START_FRAME + PHONE_GLOW_END_OFFSET,
    ],
    [0, 1, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
  );

  // Phone screen "darkens" while processing — multiplier ramps from 1.0
  // (normal) down to ~0.04 (near-black) during the processing window,
  // then back up. Smooth fade at both edges so the transition reads as
  // "screen dimmed for analysis" rather than a hard cut.
  const dimMultiplier = interpolate(
    frame,
    [
      PROCESSING_START_FRAME - 4,
      PROCESSING_START_FRAME + 4,
      PROCESSING_END_FRAME - 4,
      PROCESSING_END_FRAME + 2,
    ],
    [1, 0.04, 0.04, 1],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
  );

  // Brightness multiplier applied to the screen texture. Heavy lifting
  // for the "make screen brighter" requirement happens in the canvas
  // brightness filter at texture-load time; this multiplier just adds a
  // modest cool-blue lift during the glow window so the phone reads as
  // "lit up" when the screenshot lands. Multiplied by `dimMultiplier`
  // so the entire screen goes near-black during processing.
  const glowColor = useMemo(() => {
    const base = new Color(1.05, 1.05, 1.05);
    const peak = new Color(1.1, 1.18, 1.3);
    const lit = base.clone().lerp(peak, glowProgress);
    lit.r *= dimMultiplier;
    lit.g *= dimMultiplier;
    lit.b *= dimMultiplier;
    return lit;
  }, [glowProgress, dimMultiplier]);

  // Phone twists 360° around its OWN long axis (the vertical line of the
  // phone body, top-to-bottom of the screen) during the loading window —
  // same product-showcase motion the demo.gif uses (front → side → back
  // → side → front). The spin lives INSIDE the lay-tilt group so the
  // rotation axis inherits the tilt and matches the phone's local Y
  // rather than world Y.
  const spinProgress = interpolate(
    frame,
    [PROCESSING_START_FRAME, PROCESSING_END_FRAME],
    [0, 1],
    {
      easing: Easing.inOut(Easing.cubic),
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    },
  );
  const spinY = spinProgress * Math.PI * 2;

  return (
    <group position={worldOffset}>
      <group rotation={[PHONE_LAY_TILT, 0, 0]}>
        <group rotation={[0, spinY, 0]}>
          <RoundedBox
            radius={phoneLayout.phone.radius}
            depth={phoneLayout.phone.thickness}
            curveSegments={PHONE_CURVE_SEGMENTS}
            position={phoneLayout.phone.position}
            width={phoneLayout.phone.width}
            height={phoneLayout.phone.height}
          >
            <meshPhongMaterial color={phoneColor} shininess={PHONE_SHININESS} />
          </RoundedBox>

          <mesh position={phoneLayout.screen.position}>
            <shapeGeometry args={[screenGeometry]} />
            <meshBasicMaterial
              color={glowColor}
              toneMapped={false}
              map={activeTexture}
            />
          </mesh>
        </group>
      </group>
    </group>
  );
};
